import React, { useId } from "react";

import { getReferenceOptionLabel, splitMultilineList } from "../../../../utils/catalogProductStudioShared";
import {
  PRODUCT_STATUS_META,
  fill,
  formatDateTime,
  formatMoney,
  getColorsWithoutPhotos,
  getCoverImage,
  getProductStatusKey,
  isFilledMedia,
  summarizePrices,
  summarizeStock,
  translateBlockingIssue,
} from "../studioEditModel";
import {
  Btn,
  Card,
  Chip,
  Field,
  Icon,
  MoneyInput,
  Notice,
  Pill,
  Select,
  TextInput,
  Thumb,
  cx,
} from "../StudioUi";

const StatusRow = ({ icon, label, value, tone }) => (
  <div className="pse-kv">
    <span className="pse-kv__label">
      <Icon name={icon} size={17} className={cx("pse-kv__icon", tone && `is-${tone}`)} />
      {label}
    </span>
    <span className="pse-kv__value">{value}</span>
  </div>
);

export const buildCompletenessChecks = ({ coreForm, liveVariantDrafts, mediaDrafts, productColors, liveVariants, inventoryMode }) => {
  const filled = mediaDrafts.filter(isFilledMedia);
  const checks = [
    { key: "readyTitle", done: Boolean(String(coreForm.title || "").trim()), tab: "overview" },
    {
      key: "readyPrice",
      done:
        liveVariantDrafts.length > 0 &&
        liveVariantDrafts.every((draft) => String(draft.price ?? "").trim() !== ""),
      tab: "variants",
    },
    { key: "readyPhotos", done: filled.length > 0, tab: "media" },
    { key: "readyDescription", done: Boolean(String(coreForm.description || "").trim()), tab: "overview" },
    { key: "readyBrand", done: Boolean(coreForm.brandId), tab: "overview" },
    {
      key: "readyStock",
      done: summarizeStock(liveVariants, inventoryMode).available > 0,
      tab: "inventory",
    },
  ];
  if (productColors.length > 1) {
    checks.push({
      key: "readyColorPhotos",
      done: filled.length > 0 && getColorsWithoutPhotos(mediaDrafts, productColors).length === 0,
      tab: "media",
    });
  }
  return checks;
};

const OverviewTab = ({ studio }) => {
  const {
    tt,
    t,
    workspace,
    coreForm,
    pricingForm,
    dirty,
    brands,
    merchandisingTypes,
    productFamilyOptions,
    mediaDrafts,
    liveVariants,
    liveVariantDrafts,
    productColors,
    inventoryMode,
    updateCore,
    updatePricing,
    selectTab,
  } = studio;
  const ids = useId();
  const statusKey = getProductStatusKey(workspace);
  const statusMeta = PRODUCT_STATUS_META[statusKey];
  const filledMedia = mediaDrafts.filter(isFilledMedia);
  const cover = getCoverImage(mediaDrafts);
  const others = filledMedia.filter((item) => item !== cover);
  const stock = summarizeStock(liveVariants, inventoryMode);
  const priceSummary = summarizePrices(liveVariantDrafts);
  const enrichment = coreForm.enrichment;
  const highlights = splitMultilineList(enrichment.highlightsText);
  const badges = splitMultilineList(enrichment.badgesText);
  const optionParts = (workspace.options || []).map(
    (option) => `${option.values.length} ${option.name.toLowerCase()}`
  );
  const untitled = t("catalogProductStudio.untitled");

  const price = Number(pricingForm.price);
  const cost = Number(pricingForm.cost);
  const hasMargin = pricingForm.price !== "" && pricingForm.cost !== "" && price > 0 && Number.isFinite(cost);

  const checks = buildCompletenessChecks({
    coreForm,
    liveVariantDrafts,
    mediaDrafts,
    productColors,
    liveVariants,
    inventoryMode,
  });
  const doneCount = checks.filter((check) => check.done).length;
  const blockingIssues = workspace.publishing?.blockingIssues || [];

  const webshopRows = [
    ["dtStory", enrichment.productStory],
    ["dtMaterial", enrichment.material],
    ["dtComposition", enrichment.composition],
    ["dtCare", enrichment.careInstructions],
    ["dtFit", enrichment.fitNotes],
    ["dtSizeGuide", enrichment.sizeGuideNotes],
    ["dtStyling", enrichment.stylingNotes],
  ].filter(([, value]) => String(value || "").trim());
  const hasWebshopContent =
    webshopRows.length || highlights.length || badges.length || (enrichment.detailSections || []).length;

  return (
    <div className="pse-layout">
      <div className="pse-layout__main">
        <Card
          title={tt("ovMainInfo")}
          actions={dirty.core || dirty.pricing ? <Chip tone="warning">{tt("unsavedChip")}</Chip> : null}
        >
          <div className="pse-overview-main">
            <div className="pse-overview-media">
              <button type="button" className="pse-overview-media__hero" onClick={() => selectTab("media")}>
                {cover?.sourceUrl ? (
                  <img src={cover.sourceUrl} alt={cover.altText || coreForm.title} />
                ) : (
                  <span className="pse-overview-media__empty">
                    <Icon name="image" size={34} />
                    <span>{tt("noImage")}</span>
                  </span>
                )}
                <span className="pse-overview-media__edit" aria-hidden="true">
                  <Icon name="edit" size={15} />
                </span>
                <span className="sr-only">{tt("ovManagePhotos")}</span>
              </button>
              {filledMedia.length ? (
                <div className="pse-overview-media__strip">
                  {[cover, ...others].filter(Boolean).slice(0, 4).map((item, index) => (
                    <Thumb key={item.id || item.storageKey || index} src={item.sourceUrl} size={52} alt="" />
                  ))}
                  {filledMedia.length > 4 ? (
                    <button type="button" className="pse-overview-media__more" onClick={() => selectTab("media")}>
                      +{filledMedia.length - 4}
                    </button>
                  ) : null}
                </div>
              ) : null}
              <Btn variant="ghost" size="sm" icon="image" onClick={() => selectTab("media")}>
                {fill(tt("ovPhotoCount"), { count: filledMedia.length })} · {tt("ovManagePhotos")}
              </Btn>
            </div>

            <div className="pse-overview-fields">
              <Field
                label={tt("ovName")}
                htmlFor={`${ids}-title`}
                counter={fill(tt("charCount"), { count: coreForm.title.length, max: 160 })}
              >
                <TextInput
                  id={`${ids}-title`}
                  maxLength={160}
                  value={coreForm.title}
                  onChange={(value) => updateCore("title", value)}
                />
              </Field>

              <div className="pse-grid pse-grid--2">
                <Field label={tt("ovBrand")} htmlFor={`${ids}-brand`}>
                  <Select id={`${ids}-brand`} value={coreForm.brandId} onChange={(value) => updateCore("brandId", value)}>
                    <option value="">{tt("none")}</option>
                    {brands.map((brand) => (
                      <option key={brand.id} value={brand.id}>
                        {getReferenceOptionLabel(brand, untitled)}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label={tt("ovCategory")} htmlFor={`${ids}-family`}>
                  <Select
                    id={`${ids}-family`}
                    value={coreForm.productFamily}
                    onChange={(value) => updateCore("productFamily", value)}
                  >
                    {productFamilyOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>

              <Field label={tt("ovType")} htmlFor={`${ids}-type`} className="pse-field--half">
                <Select
                  id={`${ids}-type`}
                  value={coreForm.merchandisingTypeId}
                  onChange={(value) => updateCore("merchandisingTypeId", value)}
                >
                  <option value="">{tt("none")}</option>
                  {merchandisingTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {getReferenceOptionLabel(type, untitled)}
                    </option>
                  ))}
                </Select>
              </Field>

              <div className="pse-grid pse-grid--3">
                <Field label={tt("ovPrice")} htmlFor={`${ids}-price`}>
                  <MoneyInput id={`${ids}-price`} value={pricingForm.price} onChange={(value) => updatePricing("price", value)} />
                </Field>
                <Field label={tt("ovCompareAt")} htmlFor={`${ids}-compare`} hint={tt("ovCompareAtHint")}>
                  <MoneyInput
                    id={`${ids}-compare`}
                    value={pricingForm.compareAtPrice}
                    placeholder={tt("optional")}
                    onChange={(value) => updatePricing("compareAtPrice", value)}
                  />
                </Field>
                <Field label={tt("ovCost")} htmlFor={`${ids}-cost`} hint={tt("ovCostHint")}>
                  <MoneyInput
                    id={`${ids}-cost`}
                    value={pricingForm.cost}
                    placeholder={tt("optional")}
                    onChange={(value) => updatePricing("cost", value)}
                  />
                </Field>
              </div>
              {hasMargin ? (
                <p className="pse-muted pse-small">
                  {fill(tt("ovMargin"), {
                    amount: formatMoney(price - cost),
                    percent: `${Math.round(((price - cost) / price) * 100)}%`,
                  })}
                </p>
              ) : null}
              {liveVariantDrafts.length > 1 ? (
                <Notice tone={priceSummary.same ? "info" : "warning"} className="pse-notice--compact">
                  {priceSummary.same
                    ? fill(tt("ovPricingAppliesAll"), { count: liveVariantDrafts.length })
                    : fill(tt("ovPricingMixed"), {
                        range: `${formatMoney(priceSummary.min)} – ${formatMoney(priceSummary.max)}`,
                      })}
                </Notice>
              ) : null}

              <Field
                label={tt("ovShortDescription")}
                htmlFor={`${ids}-description`}
                hint={tt("ovShortDescriptionHint")}
                counter={String(coreForm.description.length)}
              >
                <TextInput
                  id={`${ids}-description`}
                  multiline
                  rows={4}
                  value={coreForm.description}
                  onChange={(value) => updateCore("description", value)}
                />
              </Field>

              <details className="pse-disclosure">
                <summary>
                  <Icon name="chevronRight" size={16} className="pse-disclosure__chevron" />
                  {tt("ovTechnical")}
                </summary>
                <div className="pse-disclosure__body">
                  <Field
                    label={tt("ovBaseSku")}
                    htmlFor={`${ids}-basesku`}
                    hint={tt("ovBaseSkuHint")}
                  >
                    <TextInput
                      id={`${ids}-basesku`}
                      maxLength={120}
                      value={coreForm.baseSku}
                      onChange={(value) => updateCore("baseSku", value)}
                    />
                  </Field>
                  <div className="pse-kv-list">
                    <StatusRow
                      icon="variants"
                      label={tt("ovProductMode")}
                      value={workspace.product.productMode === "variant" ? tt("ovModeVariant") : tt("ovModeSimple")}
                    />
                    <StatusRow
                      icon="cube"
                      label={tt("ovInventoryMode")}
                      value={inventoryMode === "serialized" ? tt("ovInventorySerialized") : tt("ovInventoryAggregate")}
                    />
                    <StatusRow
                      icon="layers"
                      label={tt("ovSource")}
                      value={
                        workspace.product.sourceType === "legacy_imported"
                          ? tt("ovSourceLegacy")
                          : tt("ovSourceNative")
                      }
                    />
                  </div>
                </div>
              </details>
            </div>
          </div>
        </Card>

        <Card
          title={tt("ovWebshopDetails")}
          actions={
            <Btn variant="secondary" size="sm" icon="edit" onClick={() => selectTab("details")}>
              {tt("edit")}
            </Btn>
          }
        >
          {hasWebshopContent ? (
            <div className="pse-summary-grid">
              <dl className="pse-summary-list">
                {webshopRows.map(([key, value]) => (
                  <div key={key} className="pse-summary-list__row">
                    <dt>{tt(key)}</dt>
                    <dd title={value}>{value}</dd>
                  </div>
                ))}
              </dl>
              <div className="pse-summary-side">
                {highlights.length ? (
                  <div className="pse-summary-block">
                    <span className="pse-summary-block__label">{tt("dtHighlights")}</span>
                    <ul className="pse-bullets">
                      {highlights.map((highlight) => (
                        <li key={highlight}>{highlight}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {badges.length ? (
                  <div className="pse-summary-block">
                    <span className="pse-summary-block__label">{tt("dtBadges")}</span>
                    <div className="pse-chip-row">
                      {badges.map((badge, index) => (
                        <Chip key={badge} tone={index === 0 ? "success" : "neutral"}>
                          {badge}
                        </Chip>
                      ))}
                    </div>
                  </div>
                ) : null}
                {(enrichment.detailSections || []).length ? (
                  <div className="pse-summary-block">
                    <span className="pse-summary-block__label">{tt("dtSections")}</span>
                    <div className="pse-chip-row">
                      {enrichment.detailSections.map((section, index) => (
                        <Chip key={`${section.title}-${index}`}>{section.title || "—"}</Chip>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <p className="pse-muted">{tt("ovWebshopEmpty")}</p>
          )}
        </Card>
      </div>

      <aside className="pse-layout__side">
        <Card title={tt("ovStatusCard")} actions={<Pill tone={statusMeta.tone}>{tt(statusMeta.labelKey)}</Pill>}>
          <div className="pse-kv-list">
            <StatusRow
              icon={statusKey === "published" ? "checkCircle" : "circle"}
              tone={statusKey === "published" ? "success" : "muted"}
              label={tt("ovInStore")}
              value={statusKey === "published" ? tt("ovYes") : tt("ovNo")}
            />
            <StatusRow
              icon={statusKey === "published" || statusKey === "posOnly" ? "checkCircle" : "circle"}
              tone={statusKey === "published" || statusKey === "posOnly" ? "success" : "muted"}
              label={tt("ovInPos")}
              value={statusKey === "published" || statusKey === "posOnly" ? tt("ovYes") : tt("ovNo")}
            />
            {/* Saved state, like every other row here (the toggle's draft lives in Publishing). */}
            <StatusRow
              icon={workspace.product.metaCatalogEnabled ? "checkCircle" : "circle"}
              tone={workspace.product.metaCatalogEnabled ? "success" : "muted"}
              label={tt("ovMetaCatalog")}
              value={workspace.product.metaCatalogEnabled ? tt("ovYes") : tt("ovNo")}
            />
            {workspace.publishing?.publishedAt ? (
              <StatusRow icon="globe" label={tt("ovPublishedAt")} value={formatDateTime(workspace.publishing.publishedAt)} />
            ) : null}
            <StatusRow icon="clock" label={tt("created")} value={formatDateTime(workspace.product.createdAt)} />
            <StatusRow icon="refresh" label={tt("updated")} value={formatDateTime(workspace.product.updatedAt)} />
          </div>
          <Btn variant="secondary" block icon="globe" onClick={() => selectTab("publishing")}>
            {tt("ovManagePublishing")}
          </Btn>
        </Card>

        <Card title={tt("ovReadiness")} subtitle={fill(tt("ovReadinessCopy"), { done: doneCount, total: checks.length })}>
          <div
            className="pse-progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={checks.length}
            aria-valuenow={doneCount}
          >
            <span style={{ width: `${Math.round((doneCount / checks.length) * 100)}%` }} />
          </div>
          <ul className="pse-checklist">
            {checks.map((check) => (
              <li key={check.key} className={cx("pse-checklist__item", check.done && "is-done")}>
                <Icon name={check.done ? "checkCircle" : "circle"} size={18} />
                <span>{tt(check.key)}</span>
                {!check.done ? (
                  <button type="button" className="pse-link" onClick={() => selectTab(check.tab)}>
                    {tt("edit")}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
          {blockingIssues.length ? (
            <Notice
              tone="danger"
              title={tt("pbBlockedBy")}
              action={
                <button type="button" className="pse-link" onClick={() => selectTab("publishing")}>
                  {tt("ovManagePublishing")}
                </button>
              }
            >
              <ul className="pse-bullets">
                {blockingIssues.map((issue) => (
                  <li key={issue}>{translateBlockingIssue(issue, tt)}</li>
                ))}
              </ul>
            </Notice>
          ) : null}
        </Card>

        <Card
          title={tt("ovVariantsStock")}
          actions={
            <Btn variant="ghost" size="sm" iconRight="arrowRight" onClick={() => selectTab("inventory")}>
              {tt("ovManage")}
            </Btn>
          }
        >
          <div className="pse-mini-stats">
            <div className="pse-mini-stat">
              <strong>{liveVariants.length}</strong>
              <span>{tt("ovVariants")}</span>
              {optionParts.length ? <small>{optionParts.join(" × ")}</small> : null}
            </div>
            <div className="pse-mini-stat">
              <strong>{stock.onHand}</strong>
              <span>{tt("ovOnHand")}</span>
            </div>
            <div className="pse-mini-stat pse-mini-stat--success">
              <strong>{stock.available}</strong>
              <span>{tt("ovAvailable")}</span>
            </div>
            <div className="pse-mini-stat pse-mini-stat--warning">
              <strong>{stock.reserved}</strong>
              <span>{tt("ovReserved")}</span>
            </div>
          </div>
          {stock.out || stock.low ? (
            <div className="pse-chip-row">
              {stock.out ? <Chip tone="danger">{`${stock.out} ${tt("ovOutOfStock").toLowerCase()}`}</Chip> : null}
              {stock.low ? <Chip tone="warning">{`${stock.low} ${tt("ovLowStock").toLowerCase()}`}</Chip> : null}
            </div>
          ) : null}
        </Card>

        <Card title={tt("ovQuickActions")}>
          <div className="pse-quick-actions">
            <Btn variant="secondary" icon="upload" onClick={() => selectTab("media")}>
              {tt("ovActionAddPhotos")}
            </Btn>
            <Btn
              variant="secondary"
              icon="plus"
              onClick={() => studio.openRestock()}
              disabled={!liveVariants.length}
            >
              {tt("ovActionAddStock")}
            </Btn>
            <Btn
              variant="secondary"
              icon="printer"
              onClick={() => selectTab("variants")}
              disabled={!liveVariants.some((variant) => variant.barcode)}
            >
              {tt("ovActionPrint")}
            </Btn>
            <Btn variant="secondary" icon="history" onClick={() => selectTab("history")}>
              {tt("ovActionHistory")}
            </Btn>
          </div>
        </Card>
      </aside>
    </div>
  );
};

export default OverviewTab;
